import React from 'react'
import { connect } from 'react-redux'
import ReactGA from 'react-ga'
import { toastr } from 'react-redux-toastr'
import moment from 'moment'
import { Dropdown } from 'react-bootstrap'
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table'
import _ from 'lodash'
import momentDurationFormatSetup from 'moment-duration-format'
import PropTypes from 'prop-types';
import { FaCheck } from 'react-icons/fa';

import { defaultTableOptions, paginationOptions } from '../../../helpers/table'
import momenttz from '../../../helpers/momenttz'

import { apiCall } from '../../../../action/RouterActions'

import './EventsTable.sass'
import { filterIcon } from '../../../helpers/iconHelpers'
import CustomButton from '../../../CustomButton'
import { eventIcons } from '../../../helpers/iconHelpers'

momentDurationFormatSetup(moment);

const EventsTable = (props) => {
	const {
		events,
		filters,
		page,
		sizePerPage,
		totalSize,
		openClip,
		getFleetEvents,
		setPropStates,
		toggleLoader,
	} = props;

	const selectedRow = {
		mode: 'radio',
		bgColor: '#f3f6c1', // you should give a bgcolor, otherwise, you can't regonize which row has been selected
		hideSelectColumn: true, // enable hide selection column.
		clickToSelect: true,
	}

	const handlePageChange = (argPage, argSizePerPage) => {
		getFleetEvents(argPage, argSizePerPage);
	}

	const handleSizePerPageChange = (argSizePerPage) => {
		getFleetEvents(page, argSizePerPage);
	}

	const options = {
		...paginationOptions,
		onPageChange: handlePageChange,
		onSizePerPageList: handleSizePerPageChange,
	}

	let countSelectedFilters = 0;
	let showFilterActive = false
	for (let [filter, value] of Object.entries(filters)) {
		value && countSelectedFilters++
		if (filter !== 'pending' && !value) {
			showFilterActive = true
		}
	}

	const fillFilters = (name) => {
		filters[name] = !filters[name]
		setPropStates({ filters })
		getFleetEvents()
	}

	const selectAllFilters = () => {
		if (countSelectedFilters === Object.keys(filters).length) {
			for (let key in filters) {
				filters[key] = false;
			}
			setPropStates({ filters });
			getFleetEvents();
		} else {
			for (let key in filters) {
				filters[key] = true;
			}
			setPropStates({ filters });
			getFleetEvents();
		}
	}

	const onFlagEvent = (row) => {
		const { id, flagged } = row

		toggleLoader(true)
		if (!flagged) {
			apiCall('PUT', '/events/{id}/flagged', { id })
				.then((res, err) => {
					toggleLoader(false)

					row.flagged = !row.flagged
					setPropStates({ events })
					ReactGA.event({
						category: 'Home Page',
						action: 'Flag Event',
						label: 'Sidebar'
					})
				})
				.catch((error) => {
					toggleLoader(false)
					console.log('!onFlagEvent put error:', error.response, error)

					let errDescription = 'An unexpected error occurred. Please try again later'
					if (error.response.data.response.error) {
						errDescription = error.response.data.response.error
					}
					toastr.error(errDescription)
				})
		} else {
			apiCall('DELETE', '/events/{id}/flagged', { id })
				.then((res, err) => {
					toggleLoader(false)

					row.flagged = !row.flagged
					setPropStates({ events })
					ReactGA.event({
						category: 'Home Page',
						action: 'Unflag Event',
						label: 'Sidebar'
					})
				})
				.catch((error) => {
					toggleLoader(false)
					console.log('!onFlagEvent delete error:', error.response, error)

					let errDescription = 'An unexpected error occurred. Please try again later'
					if (error.response.data.response.error) {
						errDescription = error.response.data.response.error
					}
					toastr.error(errDescription)
				})
		}
	}

	const timestampFormatter = (cell, row) => {
		if (cell) {
			return momenttz(cell).format('h:mm:ss A');
		}
		return 'Not reviewed';
	}

	const typeFormatter = (cell, row) => {
		// FWSD-4541
		if (row.event_display_type) {
			return row.event_display_type
		}

		// FWSD-4379
		if (row.uploading_status && row.uploading_status.toLowerCase() === 'video_non_exist' && row.type === 'Custom Clip') {
			return 'Custom Clip'
		}
		if (row.uploading_status && row.uploading_status.toLowerCase() === 'video_non_exist') {
			// return 'No video on device '
			return 'Video not found for this time'
		}
		// FWSD-3958
		const re = /External Driver|InputSensor2|InputSensor3|InputSensor5|InputSensor9/g;
		const typesArr = String(cell).replaceAll(re, 'Driver Event').replace('HighSpeed', 'Max Speed').split(',');
		const type = [...new Set(typesArr)].join(', ')

		return type
	}

	const driverFormatter = (cell, row) => {
		return row.driver_name || row.geotab_driver
	}

	const actionsFormatter = (cell, row) => {
		const { flagged } = row
		let playButtonAll

		const flaggedButton = <CustomButton prefix={`imageButton flaggedButton ${flagged && "on"}`} title={`${flagged ? "Unflag" : "Flag"} Event`} onClick={() => onFlagEvent(row)} />

		if (row.uploading_status === 'VIDEO_UPLOADED') {
			playButtonAll = <CustomButton prefix="imageButton playButton" title="Play Video" onClick={(e) => openClip(e, row)} />
		} else {
			playButtonAll = <CustomButton prefix="imageButton playButton" title="Play Video" disabled="disabled" />
		}

		return (
			<div>
				{playButtonAll}
				{flaggedButton}
			</div>
		)
	}

	return (
		<div className="cloud-storage vehicleSidebarTable">
			<BootstrapTable
				data={events}
				{...defaultTableOptions}
				options={options}
				remote
				fetchInfo={{ dataTotalSize: totalSize }}
				selectRow={selectedRow}
			>
				<TableHeaderColumn dataField="id" hidden isKey>Event ID</TableHeaderColumn>
				<TableHeaderColumn
					width="24%"
					dataField="timestamp"
					dataSort={false}
					dataFormat={timestampFormatter}
					tdStyle={{ whiteSpace: 'normal' }}
				>
					Time
				</TableHeaderColumn>
				<TableHeaderColumn
					width="28%"
					dataField="type"
					dataSort={false}
					tdStyle={{ whiteSpace: 'normal' }}
					dataFormat={typeFormatter}
					className="typeFilterHeader"
				>
					<div className='event-filter-wrapper'>
						Type
						<Dropdown
							id="type-filter-btn"
							drop
						>
							<Dropdown.Toggle
								// nocaret
								bsPrefix="no-caret event-filter-toggle"
							>
								{filterIcon}
								{showFilterActive
									&& <span className="flagFiltersSelected" />
								}
							</Dropdown.Toggle>
							<Dropdown.Menu
								className="typeFilterMenu"
							>
								<h5 className="category-title">Events</h5>
								<CustomButton
									variant="link"
									id="btnSelectAll"
									onClick={selectAllFilters}
								>
									{`${countSelectedFilters === Object.keys(filters).length ? 'Unselect' : 'Select'} All`}
								</CustomButton>
								<li onClick={() => fillFilters('critical_gsensor')}>
									High G-Force
									{filters.critical_gsensor && <FaCheck className='flagSelected' />}
								</li>
								<li onClick={() => fillFilters('speed')}>
									Speeding
									{filters.speed && <FaCheck className='flagSelected' />}
								</li>
								<li onClick={() => fillFilters('driver')}>
									Driver
									{filters.driver && <FaCheck className='flagSelected' />}
								</li>
								<li onClick={() => fillFilters('custom')}>
									Custom
									{filters.custom && <FaCheck className='flagSelected' />}
								</li>
								<li onClick={() => fillFilters('harshaccel')}>
									Harsh Acceleration
									{filters.harshaccel && <FaCheck className='flagSelected' />}
								</li>
								<li onClick={() => fillFilters('harshbraking')}>
									Harsh Braking
									{filters.harshbraking && <FaCheck className='flagSelected' />}
								</li>
								<li onClick={() => fillFilters('pending')}>
									Pending
									{filters.pending && <FaCheck className='flagSelected' />}
								</li>

								<h5 className="category-subtitle">DMS</h5>

								<li onClick={() => fillFilters('distraction')}>
									Distraction
									{filters.distraction && <FaCheck className='flagSelected' />}
								</li>
								<li onClick={() => fillFilters('drowsiness')}>
									Drowsiness/Head Down
									{filters.drowsiness && <FaCheck className='flagSelected' />}
								</li>
								<li onClick={() => fillFilters('yawning')}>
									Yawning
									{filters.yawning && <FaCheck className='flagSelected' />}
								</li>
								<li onClick={() => fillFilters('phone_use')}>
									Phone Use/Hand to Head
									{filters.phone_use && <FaCheck className='flagSelected' />}
								</li>
								<li onClick={() => fillFilters('seatbelt')}>
									Seatbelt
									{filters.seatbelt && <FaCheck className='flagSelected' />}
								</li>
								<li onClick={() => fillFilters('smoking')}>
									Smoking
									{filters.smoking && <FaCheck className='flagSelected' />}
								</li>
								<h5 className="category-subtitle">ADAS</h5>

								<li onClick={() => fillFilters('adas_ldw')}>
									Lane Departure
									{filters.adas_ldw && <FaCheck className='flagSelected' />}
								</li>
								<li onClick={() => fillFilters('adas_pcw')}>
									Pedestrian Warning
									{filters.adas_pcw && <FaCheck className='flagSelected' />}
								</li>
								<li onClick={() => fillFilters('adas_fcw')}>
									Forward Collision
									{filters.adas_fcw && <FaCheck className='flagSelected' />}
								</li>
								<li onClick={() => fillFilters('adas_hw')}>
									Headway Warning
									{filters.adas_hw && <FaCheck className='flagSelected' />}
								</li>
								<li onClick={() => fillFilters('tailgating')}>
									Tailgating
									{filters.tailgating && <FaCheck className='flagSelected' />}
								</li>
								<Dropdown.Item className="closeDropdown">
									<svg width="18" height="18" fill="currentColor" viewBox="0 0 320 512" xmlns="http://www.w3.org/2000/svg"><path d="M177 159.7l136 136c9.4 9.4 9.4 24.6 0 33.9l-22.6 22.6c-9.4 9.4-24.6 9.4-33.9 0L160 255.9l-96.4 96.4c-9.4 9.4-24.6 9.4-33.9 0L7 329.7c-9.4-9.4-9.4-24.6 0-33.9l136-136c9.4-9.5 24.6-9.5 34-.1z" /></svg>
								</Dropdown.Item>
							</Dropdown.Menu>
						</Dropdown>
					</div>
				</TableHeaderColumn>
				<TableHeaderColumn
					width="30%"
					dataField="driver_name"
					dataSort={false}
					dataFormat={driverFormatter}
					tdStyle={{ whiteSpace: 'normal' }}
				>
					Driver
				</TableHeaderColumn>
				<TableHeaderColumn
					width="18%"
					dataFormat={actionsFormatter}
					tdStyle={{ whiteSpace: 'normal' }}
				>
					<div className="custom-table-header-row">
						<div className="custom-table-header-title" />
					</div>
				</TableHeaderColumn>
			</BootstrapTable>
		</div>
	)
}

EventsTable.propTypes = {
	events: PropTypes.arrayOf(PropTypes.any).isRequired,
	filters: PropTypes.objectOf(PropTypes.any).isRequired,
	openClip: PropTypes.func.isRequired,
	getFleetEvents: PropTypes.func.isRequired,
	setPropStates: PropTypes.func.isRequired
};

export default connect(
	null,
	dispatch => ({
		toggleLoader: (show) => {
			dispatch({ type: 'TOGGLE_LOADER', payload: show });
		}
	})
)(EventsTable);
